import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import ParkingCard from "../components/ParkingCard";
import { Heart, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { API } from "../utils/api";

export default function Favorites() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [spots, setSpots] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchFavorites();
  }, [user]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const [spotsRes, idsRes] = await Promise.all([
        axios.get(`${API}/favorites`),
        axios.get(`${API}/favorites/ids`)
      ]);
      setSpots(spotsRes.data);
      setFavoriteIds(idsRes.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (spotId) => {
    try {
      await axios.delete(`${API}/favorites/${spotId}`);
      setFavoriteIds(prev => prev.filter(id => id !== spotId));
      setSpots(prev => prev.filter(s => s.id !== spotId));
      toast.success("Eliminado de favoritos");
    } catch (e) {
      toast.error("Error al eliminar de favoritos");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-red-400 fill-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Mis Favoritos</h1>
              <p className="text-gray-500 text-sm">{spots.length} {spots.length === 1 ? 'plaza guardada' : 'plazas guardadas'}</p>
            </div>
          </div>
        </div>

        {/* Empty state */}
        {spots.length === 0 && (
          <div className="text-center py-20">
            <Heart className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No tienes favoritos</h2>
            <p className="text-gray-500 mb-6">Explora plazas y pulsa el corazón para guardarlas aquí</p>
            <button
              onClick={() => navigate("/explore")}
              className="px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-white rounded-xl font-medium transition-colors"
            >
              Explorar plazas
            </button>
          </div>
        )}

        {/* Grid */}
        {spots.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {spots.map(spot => (
              <ParkingCard 
                key={spot.id} 
                spot={spot} 
                isFavorite={favoriteIds.includes(spot.id)} 
                onToggleFavorite={toggleFavorite} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
